/**
 * Pure helpers for the family share page (pages/TrackRide.js). No React, no
 * Maps, so they run under plain `node --test` (trackShare.test.js).
 */

/** Which stage the page shows, from the share doc's status. */
export const trackStage = (share) => {
  if (!share) return 'ended';
  const s = share.status;
  if (s === 'cancelled') return 'cancelled';
  if (!share.active || s === 'completed') return 'ended';
  if (s === 'started') return 'ontrip';
  if (s === 'arrived') return 'arrived';
  if (s === 'driver_assigned') return 'arriving';
  return 'searching';
};

/** A live ETA older than this is not shown (the auto may have stopped sending). */
export const SHARE_ETA_FRESH_MS = 3 * 60 * 1000;

/**
 * Minutes to show in the big ETA pill, or null. Uses the server's live ETA
 * (publicTracks etaMin/etaPhase/etaAt) only when it is fresh and for the leg
 * the page is showing: pickup while arriving, drop while on the trip.
 */
export const liveEtaMinutes = (track, stage, now) => {
  const wantPhase = stage === 'arriving' ? 'pickup' : (stage === 'ontrip' ? 'drop' : null);
  if (!wantPhase || !track || track.etaPhase !== wantPhase) return null;
  const eta = Number(track.etaMin);
  const at = Number(track.etaAt);
  if (track.etaMin == null || !Number.isFinite(eta) || eta < 0) return null;
  // `now` should be server time (Date.now() + the Firebase server offset), as
  // etaAt is server-stamped. A clock still a little behind just means no
  // countdown yet, never a longer ETA.
  const age = Math.max(0, now - at);
  if (!Number.isFinite(at) || age > SHARE_ETA_FRESH_MS) return null;
  // The estimate was made at etaAt; count down since then, but never show 0.
  const left = eta - age / 60000;
  return Math.max(1, Math.round(left));
};

/**
 * How to describe the vehicle. The Anna app pre-fills the model as
 * "Bajaj RE Auto", so a model that already says "auto" is shown on its own
 * ("Bajaj RE Auto"), a plain "auto" adds nothing, and anything else gets the
 * type in front ("Auto rickshaw · Piaggio Ape").
 */
export const vehicleLabel = (model) => {
  const m = String(model || '').trim();
  if (!m || /^auto(\s*rickshaw)?$/i.test(m)) return { kind: 'type', model: '' };
  if (/\bauto\b/i.test(m)) return { kind: 'model', model: m };
  return { kind: 'typeAndModel', model: m };
};

/** When the auto should arrive, as a timestamp, from minutes left. */
export const arriveAroundMs = (minutes, now) => (
  Number.isFinite(minutes) ? now + minutes * 60000 : null
);

/**
 * The rider's first name for the title, defensively: the server already
 * filters it, but a doc from an older server, or junk, must never put a
 * number in the title.
 */
export const shareTitleName = (name) => {
  const n = String(name || '').trim();
  if (n.length < 2 || n.length > 20 || /[\d@]/.test(n)) return '';
  return n;
};

/**
 * Which two page-title strings to use for this share: an Intertown trip says so ("Abraz's Intertown
 * trip"), everything else, including a document from before Intertown links existed, keeps "auto ride".
 */
export const shareTitleKeys = (share) => (share?.kind === 'intertown'
  ? { named: 'track.titleNamedIntertown', anon: 'track.titleAnonIntertown' }
  : { named: 'track.titleNamed', anon: 'track.titleAnon' });

const toPoint = (p) => {
  if (!p) return null;
  const lat = typeof p.lat === 'function' ? p.lat() : Number(p.lat);
  const lng = typeof p.lng === 'function' ? p.lng() : Number(p.lng);
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
};

/**
 * Splits a route at the point nearest the auto: `done` (already driven, drawn
 * grey) and `ahead` (still to go, drawn green). Both include the split point
 * so the two lines meet. `index` lets callers redraw only when it moves.
 * Flat-earth distance is plenty at town scale.
 */
export const splitRouteAt = (path, at) => {
  const pts = (path || []).map(toPoint).filter(Boolean);
  const p = toPoint(at);
  if (pts.length < 2 || !p) return { done: [], ahead: pts, index: 0 };
  const k = Math.cos((p.lat * Math.PI) / 180);
  let best = 0;
  let bestD = Infinity;
  pts.forEach((q, i) => {
    const dx = (q.lng - p.lng) * k;
    const dy = q.lat - p.lat;
    const d = dx * dx + dy * dy;
    if (d < bestD) { bestD = d; best = i; }
  });
  return { done: pts.slice(0, best + 1), ahead: pts.slice(best), index: best };
};

/**
 * A share link works for 12 hours after its trip ends (server deletes the doc
 * after that: firebase/functions/shareLink.js SHARE_LINK_TTL_MS; keep equal).
 */
export const SHARE_LINK_TTL_MS = 12 * 60 * 60 * 1000;

const tsMs = (v) => {
  if (!v) return 0;
  if (typeof v.toMillis === 'function') return v.toMillis();
  if (Number.isFinite(v.seconds)) return v.seconds * 1000;
  if (Number.isFinite(v._seconds)) return v._seconds * 1000;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * What an ended link shows: 'completed' or 'cancelled' (with the end time, no
 * place), or 'expired' once SHARE_LINK_TTL_MS has passed, even if the server
 * has not deleted the doc yet. null while the trip is live.
 */
export const endedView = (share, now) => {
  const stage = trackStage(share);
  if (stage !== 'ended' && stage !== 'cancelled') return null;
  const endedMs = tsMs(share?.endedAt);
  if (endedMs && now - endedMs > SHARE_LINK_TTL_MS) return { kind: 'expired', endedMs };
  return { kind: stage === 'cancelled' ? 'cancelled' : 'completed', endedMs };
};

/** Tag on the share page's Book button, so Play Console shows its installs. */
export const SHARE_PAGE_SOURCE = 'share_page';

/**
 * Where "Book your own auto" goes once the trip has ended. On the website it
 * is /app, which opens our Play Store page on Android and the website
 * elsewhere (iPhone, computer). Inside our own app it just goes home.
 */
export const bookOwnHref = ({ isNative = false } = {}) => (
  isNative ? '/' : `/app?utm_source=${SHARE_PAGE_SOURCE}`
);
